import * as React from 'react';
import Box from '@mui/material/Box';
import styles from 'features/mite-policy/step-1/StepOne.module.css';
import Typography from '@mui/material/Typography';
import {
    Accordion,
    AccordionSummary,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    IconButton,
    AccordionDetails,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { updateField } from 'entities/mite-policy/step-1';
import { useFormRedux } from "shared/utils/hooks/useFormRedux";
import { validationSchema } from 'entities/mite-policy/step-1/utils/validationSchema';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { fetchDefaultId, fetchDictionaries, fetchPrograms, fetchStepOne } from 'entities/mite-policy/step-1/action/fetch-step-one';
import { DefaultId, DefaultIdError, DictionariesPrograms, DictionariesRegions, FormFieldsStepOne, isLoadingDefaultId, ProgramsDictionariesForRegion } from 'entities/mite-policy/step-1/selector';
import { BasicAutocomplete } from 'shared/basic-autocomplete/BasicAutocomplete';
import { colors } from 'app/providers/theme/colors-type';
import CustomButton from 'shared/custom-button/CustomButton';
import { useEffect, useState } from 'react';
import Banner from 'shared/assets/Banner';
import UgskLogoWhite from 'shared/assets/UgskLogoWhite';
import { doNotShowRegion, ProductID, Programs, Regions, SellerID } from 'entities/main-page/selector';
import { useLocation } from 'react-router-dom';

interface IPropsStepOne {
    windowWidth: number;
    ID: string;
}

export const StepOne: React.FC<IPropsStepOne> = ({ windowWidth, ID }) => {
    const dispatch = useAppDispatch();
    const {
        formState,
        handleChange,
        handleBlur,
        errors,
        handleAutocompleteChange,
        validateForm,
        handleCheckboxChange,
        handleAutocompleteChangeID
    } = useFormRedux(
        (state) => state.stepOne,
        updateField,
        validationSchema
    );
    const location = useLocation();
    const isOldLink = location.state?.productIdUrl;
    const programs = useAppSelector(Programs);
    const regions = useAppSelector(Regions);
    const sellerID = useAppSelector(SellerID);
    const productID = useAppSelector(ProductID);
    const stepOneForm = useAppSelector(FormFieldsStepOne);


    const defaultId = useAppSelector(DefaultId);
    const loadingDefaultId = useAppSelector(isLoadingDefaultId);
    const errorDefaultId = useAppSelector(DefaultIdError);

    useEffect(() => {
        if (!ID && !defaultId) {
            dispatch(fetchDefaultId());
        }
    }, [])

    const handleGetSum = async () => {
        const isValid = await validateForm();
        if (isValid) {
            dispatch(fetchStepOne({
                productId: productID,
                insuredCount: formState.InsuranceCount,
                city: formState.City,
                promocode: formState.Promocode,
                programId: formState.programId,
                projectId: stepOneForm.Uid,
                sellerId: sellerID,
            }));
        }
    };

    const programsSelector = useAppSelector(ProgramsDictionariesForRegion);
    const getPrograms = () => {
        if (!isOldLink) {
            dispatch(fetchPrograms({
                regionId: formState.City,
                id: ID ? ID : defaultId,
            }));
        }
    }

    useEffect(() => {
        if (formState.City && (ID || defaultId)) {
            getPrograms();
        }

    }, [formState.City, defaultId, ID])

    const getBlurPromocode = () => {
        dispatch(updateField({ field: 'Price', value: 0 }));
    };

    const handleIncrement = () => {
        if (formState.InsuranceCount < 5) {
            const newValue = (formState.InsuranceCount || 0) + 1;
            dispatch(updateField({ field: 'InsuranceCount', value: newValue }));
            dispatch(updateField({ field: 'Price', value: 0 }));
            dispatch(updateField({ field: 'Sum', value: 0 }));
        }

    };

    const handleDecrement = () => {
        const newValue = Math.max((formState.InsuranceCount || 0) - 1, 0);
        dispatch(updateField({ field: 'InsuranceCount', value: newValue }));
        dispatch(updateField({ field: 'Price', value: 0 }));
        dispatch(updateField({ field: 'Sum', value: 0 }));
    };

    const [textAccordion, setTextAccordion] = useState<string[]>([]);

    useEffect(() => {
        if (isOldLink) {
            if (formState.programId) {
                const selectedProgram = programs.find(
                    (program) => program.uid === formState.programId
                );
                if (selectedProgram) {
                    setTextAccordion(selectedProgram.risks || []);
                } else {
                    setTextAccordion([]);
                }
            }
        } else {
            if (formState.programId) {
                const selectedProgram = programsSelector.Programs.find(
                    (program) => program.uid === formState.programId
                );
                if (selectedProgram) {
                    setTextAccordion(selectedProgram.risks || []);
                } else {
                    setTextAccordion([]);
                }
            }
        }

    }, [formState.programId, programsSelector.Programs, programs]);

    useEffect(() => {
        if (regions.length === 1) {
            dispatch(updateField({ field: 'City', value: regions[0].uid }));
        }
    }, [regions])
    useEffect(() => {
        if (isOldLink) {
            if (programs.length === 1) {
                dispatch(updateField({ field: 'programId', value: programs[0].uid }));
            }
        } else {
            if (programsSelector.Programs.length === 1) {
                dispatch(updateField({ field: 'programId', value: programsSelector.Programs[0].uid }));
            }
        }

    }, [programsSelector.Programs, programs])

    useEffect(() => {
        if (formState.programId && formState.City) {
            handleGetSum();
        }
    }, [formState.programId, formState.InsuranceCount, formState.City, formState.Promocode])
    const formatNumberWithSpaces = (number: number): string => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    const doNotShowRegionProps = useAppSelector(doNotShowRegion);

    if (windowWidth <= 700) {
        return (
            <Box>
                {formState.Sum > 0 && (
                    <Box className={styles.colorBox}>
                        <span className={styles.icon}><InfoIcon /></span>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', 'gap': '10px' }}>
                            <Typography component="p" sx={{ margin: 0 }}>
                                Страховая сумма
                            </Typography>
                            <span className={styles.iconRub}>
                                {formatNumberWithSpaces(formState.Sum)} <CurrencyRubleIcon fontSize="small" />
                            </span>
                        </Box>
                    </Box>
                )}

                {!doNotShowRegionProps && (
                    <Box sx={{ mt: 2 }}>
                        {regions.length > 0 && (
                            <BasicAutocomplete
                                value={formState.City}
                                onChange={(field, value: string) => {
                                    dispatch(updateField({ field: 'City', value: value }));
                                }}
                                onInputChange={(field, value: string) => {
                                    handleAutocompleteChangeID('City')(value);
                                }}
                                options={regions}
                                error={errors.City}
                                label="Ваш город"
                                noOptionsText="Город не найден"
                                name='City'
                                disabled={regions.length === 1}
                            />
                        )}
                    </Box>
                )}


                <Box className={styles.countBox}>
                    <IconButton onClick={handleDecrement} disabled={formState.InsuranceCount <= 1}>
                        <RemoveIcon />
                    </IconButton>
                    <p className={styles.text}>
                        <span>{formState.InsuranceCount || 1} </span> {formState.InsuranceCount === 1 ? 'застрахованный' : 'застрахованных'}
                    </p>
                    <IconButton onClick={handleIncrement} disabled={formState.InsuranceCount === 5}>
                        <AddIcon />
                    </IconButton>
                </Box>

                <Box sx={{ mt: 2 }}>
                    {isOldLink ? (
                        <>
                            {programs.length > 0 && (
                                <BasicAutocomplete
                                    value={formState.programId}
                                    onChange={(field, value: string) => {
                                        dispatch(updateField({ field: 'programId', value: value }));
                                    }}
                                    onInputChange={(field, value: string) => {
                                        handleAutocompleteChangeID('programId')(value);
                                    }}
                                    options={programs}
                                    error={errors.programId}
                                    label="Ваша программа"
                                    noOptionsText="Программа не найдена"
                                    name='programId'
                                    disabled={programs.length === 1}
                                />
                            )}</>
                    ) : (
                        <>
                            {programsSelector.isLoadingPrograms ? (
                                <CircularProgress color="inherit" size={20} />
                            ) : (
                                <>
                                    {programsSelector.Programs.length > 0 && (
                                        <BasicAutocomplete
                                            value={formState.programId}
                                            onChange={(field, value: string) => {
                                                dispatch(updateField({ field: 'programId', value: value }));
                                            }}
                                            onInputChange={(field, value: string) => {
                                                handleAutocompleteChangeID('programId')(value);
                                            }}
                                            options={programsSelector.Programs}
                                            error={errors.programId}
                                            label="Ваша программа"
                                            noOptionsText="Программа не найдена"
                                            name='programId'
                                            disabled={programsSelector.Programs.length === 1}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Box>

                <Box className={styles.accordionBox}>
                    {textAccordion.length > 0 && (
                        <Accordion
                            sx={{
                                '& .MuiAccordionSummary-root': {
                                    minHeight: '32px',
                                    padding: '0 8px',
                                    margin: 0,
                                    '& .MuiTypography-root': {
                                        fontSize: '17px',
                                        fontWeight: 'bold',
                                        color: colors.primary
                                    },
                                    '&.Mui-expanded': {
                                        minHeight: '32px',
                                    },
                                },
                                '& .MuiAccordionDetails-root': {
                                    padding: '0 8px 8px',
                                },
                                '& .MuiAccordionSummary-content': {
                                    margin: '0',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                            >
                                <Typography component="span">Что входит в полис</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List dense sx={{ paddingTop: 0 }}>
                                    {textAccordion.map((item, index) => (
                                        <ListItem disablePadding key={index} sx={{ paddingLeft: '8px' }}>
                                            <ListItemIcon className={styles.iconList}>
                                                <FiberManualRecordIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={item} sx={{ margin: 0 }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>

                <Box className={styles.priceBox}>
                    {formState.Price !== 0 && (
                        <p>Сумма - <span className={styles.spanPrice}>{formState.Price}</span> <span className={styles.spanIcon}><CurrencyRubleIcon /></span></p>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box className={styles.desktopContainer}>
            <Box className={styles.desktopBanner}>
                <Box className={styles.desktopBannerTitle}>
                    <UgskLogoWhite />
                    <Box className={styles.title}>
                        <h2>Антиклещ</h2>
                        <h4>Для клиента</h4>
                    </Box>
                </Box>
                <Box className={styles.desktopBannerImg}>
                    <Banner />
                </Box>


            </Box>
            <Box>

                {formState.Sum > 0 && (
                    <Box className={styles.colorBox}>
                        <span className={styles.icon}><InfoIcon /></span>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', 'gap': '10px' }}>
                            <Typography component="p" sx={{ margin: 0 }}>
                                Страховая сумма
                            </Typography>
                            <span className={styles.iconRub}>
                                {formatNumberWithSpaces(formState.Sum)} <CurrencyRubleIcon fontSize="small" />
                            </span>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box className={styles.desktopBox}>
                {!doNotShowRegionProps && (
                    <>
                        {regions.length > 0 && (
                            <BasicAutocomplete
                                value={formState.City}
                                onChange={(field, value: string) => {
                                    dispatch(updateField({ field: 'City', value: value }));
                                }}
                                onInputChange={(field, value: string) => {
                                    handleAutocompleteChangeID('City')(value);
                                }}
                                options={regions}
                                error={errors.City}
                                label="Ваш город"
                                noOptionsText="Город не найден"
                                name='City'
                                disabled={regions.length === 1}
                            />
                        )}
                    </>
                )}

                <Box className={styles.desktopCountBox}>
                    <IconButton onClick={handleDecrement} disabled={formState.InsuranceCount <= 1}>
                        <RemoveIcon />
                    </IconButton>
                    <p className={styles.text}>
                        <span>{formState.InsuranceCount || 1} </span> {formState.InsuranceCount === 1 ? 'застрахованный' : 'застрахованных'}
                    </p>
                    <IconButton onClick={handleIncrement} disabled={formState.InsuranceCount === 5}>
                        <AddIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                {isOldLink ? (
                    <>
                        {programs.length > 0 && (
                            <BasicAutocomplete
                                value={formState.programId}
                                onChange={(field, value: string) => {
                                    dispatch(updateField({ field: 'programId', value: value }));
                                }}
                                onInputChange={(field, value: string) => {
                                    handleAutocompleteChangeID('programId')(value);
                                }}
                                options={programs}
                                error={errors.programId}
                                label="Ваша программа"
                                noOptionsText="Программа не найдена"
                                name='programId'
                                disabled={programs.length === 1}
                            />
                        )}</>
                ) : (
                    <>
                        {programsSelector.isLoadingPrograms ? (
                            <CircularProgress color="inherit" size={20} />
                        ) : (
                            <>
                                {programsSelector.Programs.length > 0 && (
                                    <BasicAutocomplete
                                        value={formState.programId}
                                        onChange={(field, value: string) => {
                                            dispatch(updateField({ field: 'programId', value: value }));
                                        }}
                                        onInputChange={(field, value: string) => {
                                            handleAutocompleteChangeID('programId')(value);
                                        }}
                                        options={programsSelector.Programs}
                                        error={errors.programId}
                                        label="Ваша программа"
                                        noOptionsText="Программа не найдена"
                                        name='programId'
                                        disabled={programsSelector.Programs.length === 1}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
            <Box sx={{ mb: 2 }}>
                {textAccordion.length > 0 && (
                    <Accordion
                        sx={{
                            '& .MuiAccordionSummary-root': {
                                minHeight: '35px',
                                padding: '0 8px',
                                margin: '20px 0 0 0',
                                '& .MuiTypography-root': {
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: colors.primary
                                },
                                '&.Mui-expanded': {
                                    minHeight: '35px',
                                },
                            },
                            '& .MuiAccordionDetails-root': {
                                padding: '0 8px 8px',
                            },
                            '& .MuiAccordionSummary-content': {
                                margin: '0',
                            },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography component="span">Что входит в полис</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense sx={{ paddingTop: 0 }}>
                                {textAccordion.map((item, index) => (
                                    <ListItem disablePadding key={index} sx={{ paddingLeft: '8px' }}>
                                        <ListItemIcon className={styles.iconList}>
                                            <FiberManualRecordIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={item} sx={{ margin: 0 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )}

            </Box>


            <Box className={styles.desktopPromoBox}>
                {/* <TextField
                    name="Promocode"
                    label="Промокод"
                    value={formState.Promocode}
                    onChange={handleChange('Promocode')}
                    onBlur={(e) => {
                        handleBlur('Promocode');
                        getBlurPromocode();
                    }}
                    error={!!errors.Promocode}
                    helperText={errors.Promocode}
                /> */}
                {/* <Box className={styles.desktopBtnGetSum}>
                    {stepOneForm.isLoadingCalc ? (
                        <CircularProgress color="inherit" size={20} />
                    ) : (
                        <>
                            {formState.Price === 0 && (
                                <CustomButton
                                    title="Рассчитать"
                                    handleClick={handleGetSum}
                                    textColor="white"
                                    backgroundColor={colors.primary}
                                    width="150px"
                                    gap="10px"
                                    borderRadius='4px'
                                    padding='3px'
                                />
                            )}
                        </>
                    )}
                </Box> */}

            </Box>
            <Box className={styles.priceBox}>
                {formState.Price !== 0 && (
                    <p>Сумма - <span className={styles.spanPrice}>{formState.Price}</span> <span className={styles.spanIcon}><CurrencyRubleIcon /></span></p>
                )}
            </Box>
        </Box>
    );
};
